import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { fetchStockStatus } from '@theme/bbd-stock-status-v2';

const SEVERITIES = ['in-store', 'not-in-store', 'outofstock'];

/**
 * @typedef {{ id?: string|number, title?: string, sku?: string }} Variant
 */

/**
 * Store card on the PDP. Heading, address and call to action are server-rendered,
 * the store stock line and note come from the v2 stock status API.
 */
class BbdStoreStockStatus extends Component {
  requiredRefs = ['line', 'label', 'note', 'reserveAction', 'reserveButton', 'reserveLabel'];

  /** @type {HTMLElement|null} */
  #section = null;

  #requestId = 0;

  /** @type {Variant|null} */
  #variant = null;

  connectedCallback() {
    super.connectedCallback();
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.refs.reserveButton.addEventListener('click', this.#handleReserveClick);
    this.#update({ id: this.dataset.variantId, title: this.dataset.variantTitle, sku: this.dataset.sku });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#section?.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.refs.reserveButton?.removeEventListener('click', this.#handleReserveClick);
  }

  /**
   * @param {Event & { detail: { resource: { sku?: string }|null, data?: { productId?: string, newProduct?: { id: string } } } }} event
   */
  #handleVariantUpdate = (event) => {
    const { resource, data } = event.detail;

    if (data?.newProduct) {
      this.dataset.productId = data.newProduct.id;
    } else if (data?.productId && data.productId !== this.dataset.productId) {
      return;
    }

    this.#update(resource);
  };

  /**
   * @param {Variant|null} variant - The selected variant, null when the option combination does not exist
   */
  async #update(variant) {
    const requestId = ++this.#requestId;
    this.#variant = variant;

    if (!variant) {
      this.#render(null);
      return;
    }

    const stockStatus = await fetchStockStatus(variant, this.dataset.productHandle);

    // A newer variant was selected while this request was pending
    if (requestId !== this.#requestId) return;

    this.#render(stockStatus);
  }

  /**
   * Without a stock status (API error, unknown variant) only the server-rendered parts remain visible.
   * @param {import('./bbd-stock-status-v2.js').StockStatus|null} stockStatus
   */
  #render(stockStatus) {
    const experience = stockStatus?.experience;

    SEVERITIES.forEach((severity) => this.classList.remove(`bbd-store-stock-status--${severity}`));

    this.refs.label.textContent = experience?.message ?? '';
    this.refs.line.hidden = !experience?.message;
    this.refs.note.textContent = experience?.note ?? '';
    this.refs.note.hidden = !experience?.note;

    if (this.refs.address) {
      this.refs.address.hidden = experience ? !experience.showAddress : false;
    }

    if (stockStatus) {
      this.dataset.status = stockStatus.status;
    } else {
      delete this.dataset.status;
    }

    if (SEVERITIES.includes(experience?.severity)) {
      this.classList.add(`bbd-store-stock-status--${experience.severity}`);
    }

    // The reserve button replaces the call to action, but only when the reserve form is on the page
    const action = experience?.action;
    const canReserve = action?.type === 'reserve' && Boolean(this.#reserveDialog);
    this.refs.reserveLabel.textContent = canReserve ? action.label : '';
    this.refs.reserveAction.hidden = !canReserve;

    // Sold out: trying the instrument in the store is not possible, so no call to action
    const isSoldOut = stockStatus?.status === 'out_of_stock';
    if (this.refs.cta) this.refs.cta.hidden = canReserve || isSoldOut;
  }

  /** @returns {HTMLDialogElement|null} The dialog from snippets/bbd-reserve-form.liquid (MAI-156) */
  get #reserveDialog() {
    return this.querySelector('dialog[data-bbd-reserve-dialog]');
  }

  #handleReserveClick = () => {
    const dialog = this.#reserveDialog;
    if (!dialog || !this.#variant) return;

    const productUrl = new URL(this.dataset.productUrl ?? '', window.location.origin);
    if (this.#variant.id) productUrl.searchParams.set('variant', String(this.#variant.id));

    this.#setField(dialog, 'contact[Product]', this.dataset.productTitle);
    // 'Default Title' is noise for products without options
    const variantTitle = 'onlyDefaultVariant' in this.dataset ? '' : this.#variant.title;
    this.#setField(dialog, 'contact[Variant]', variantTitle);
    this.#setField(dialog, 'contact[SKU]', this.#variant.sku);
    this.#setField(dialog, 'contact[Link]', productUrl.href);

    dialog.showModal();
  };

  /**
   * @param {HTMLElement} dialog
   * @param {string} name - Name of the hidden input
   * @param {string|undefined} value
   */
  #setField(dialog, name, value) {
    const input = dialog.querySelector(`input[name="${name}"]`);
    if (input instanceof HTMLInputElement) input.value = value ?? '';
  }
}

if (!customElements.get('bbd-store-stock-status')) {
  customElements.define('bbd-store-stock-status', BbdStoreStockStatus);
}
